import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';

import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';

import Send from '@mui/icons-material/Send';
import Close from '@mui/icons-material/Close';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import Person from '@mui/icons-material/Person';
import Source from '@mui/icons-material/Source';

import Refresh from '@mui/icons-material/Refresh';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  chatbotService,
  CadCheckRef,
  ChatMessage,
  ConversationMessage,
} from '../../services/chatbotService';
import { authService } from '../../services/authService';
import { notificationService } from '../../services/notificationService';
import { CallDetails } from '../../types/Call';
import { useCallContext, CadChatApi, ChatScope } from '../../contexts/CallContext';
import { CadCheckMessage, CadCheckView } from './CadCheckMessage';
import { CadPollState } from '../../hooks/useCadRecord';
import { CadReplaceDialog, HiddenPdfInput } from '../calldetails/CadRecordParts';
import { CadApplyOutcome, tally, validatePdf } from '../calldetails/cadRecordDisplay';

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
const MAX_WIDTH = 800;

// Starter questions per scope, shown while the conversation is still empty.
const SUGGESTED_PROMPTS: Record<ChatScope, string[]> = {
  call: [
    'What could the operator have improved on this call?',
    'Summarize this call and what drove its score',
    'What training would help based on this call?',
  ],
  all: [
    'What do the training standards say about handling a distressed caller?',
    'What are common performance issues across calls?',
  ],
  results: [
    'Which operator has the lowest average category score?',
    'What is the lowest-scoring category across these results?',
    'Which call has the lowest overall score?',
  ],
};

/** The assistant's CAD entry point (leg 5): one action, one attach control. */
const CAD_CHECK_MESSAGE = 'Check the CAD record for this call';

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Starter prompts and the CAD action wrap inside the pane rather than overflow it. */
const suggestionChipSx = {
  height: 'auto',
  maxWidth: '100%',
  fontSize: '0.75rem',
  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
} as const;

/** What a CAD check message says right now: a stage in progress, a plain reply, or the check. */
type CadStage =
  | { kind: 'progress'; text: string; detail?: string }
  | { kind: 'reply'; content: string }
  | { kind: 'check'; check: CadCheckView; content: string };

/** A wait that gave up, as the tab words it; null while the wait is still on. */
const pollProblem = (state: CadPollState, subject: string, status: string): CadStage | null => {
  if (state === 'timedOut') {
    return {
      kind: 'reply',
      content: `${subject} has taken more than 5 minutes. It is still queued on the server; the CAD Record tab shows the result when it lands.`,
    };
  }
  if (state === 'unavailable') {
    return { kind: 'reply', content: `${status} could not be read. Refresh the page or sign in again.` };
  }
  return null;
};

/** The assistant's reply to a CAD check, from the record's live state. */
const resolveCadStage = (ref: CadCheckRef, entry: CadChatApi): CadStage => {
  const { cad, canEdit } = entry;

  if (ref.attaching) {
    return {
      kind: 'progress',
      text: 'Attaching the CAD record…',
      detail: cad.uploadProgress !== null ? `${cad.uploadProgress}%` : undefined,
    };
  }

  const record = ref.attachmentId && cad.record?.attachmentId === ref.attachmentId ? cad.record : undefined;
  if (!record) {
    return {
      kind: 'reply',
      content: ref.attachmentId
        ? 'The CAD record this check was for has since been replaced. Ask me to check the CAD record again to see the current one.'
        : canEdit
          ? 'No CAD record is attached to this call. Attach the CAD event report PDF and I will read it and check it against the call.'
          : 'No CAD record is attached to this call. A supervisor can attach the CAD event report.',
    };
  }

  switch (record.extractionStatus) {
    case 'Pending':
    case 'Extracting':
      return (
        pollProblem(cad.pollState, 'Reading the CAD report', 'The reading status') ?? {
          kind: 'progress',
          text:
            record.extractionStatus === 'Pending'
              ? 'Queued to read the CAD report…'
              : 'Reading the CAD report…',
        }
      );
    case 'ExtractionFailed':
      return {
        kind: 'reply',
        content: `The CAD report could not be read. ${record.extractionError || 'Extraction failed.'} Replace the file, or try reading it again from the CAD Record tab.`,
      };
    case 'Extracted':
      break;
  }

  const check: CadCheckView = {
    attachmentId: record.attachmentId,
    fileName: record.originalFileName,
    dismissed: ref.dismissed,
  };
  const notChecked = (reason: string): CadStage => ({
    kind: 'check',
    check: { ...check, checkFailed: reason },
    content: `CAD record ${record.originalFileName} read; ${reason}`,
  });

  switch (record.verificationStatus) {
    case 'Pending':
    case 'Verifying':
      return (
        pollProblem(cad.verificationPollState, 'The check against the call', 'The check status') ?? {
          kind: 'progress',
          text:
            record.verificationStatus === 'Pending'
              ? 'Report read. Queued to check the CAD entry against the call…'
              : 'Report read. Checking the CAD entry against the call…',
        }
      );
    case 'NotRun':
      // The poll keeps reading for a while after the report is read, for an automatic check
      // that may be about to start. Only once it settles is the check known not to be coming.
      if (cad.pollState === 'polling') {
        return { kind: 'progress', text: 'Report read. Waiting for the check against the call…' };
      }
      if (!canEdit) {
        return notChecked(
          'the CAD entry has not been checked against the call yet. A supervisor can run the check from the CAD Record tab.'
        );
      }
      if (ref.requestFailed) {
        return notChecked(
          'the check against the call could not be started. You can run it from the CAD Record tab.'
        );
      }
      return { kind: 'progress', text: 'Report read. Requesting the check against the call…' };
    case 'VerificationFailed':
      return notChecked(
        `the check against the call could not be completed. ${record.verificationError || 'Verification failed.'} You can run it again from the CAD Record tab.`
      );
    case 'Verified':
      if (cad.verificationLoadError) {
        return notChecked(
          'the check finished, but its result could not be loaded. Open the CAD Record tab and choose Load again.'
        );
      }
      if (!cad.verification) {
        return { kind: 'progress', text: 'Report read and checked. Loading the result…' };
      }
      {
        const counts = tally(cad.verification.items);
        return {
          kind: 'check',
          check,
          content: `CAD record ${record.originalFileName} read and checked against the call${counts ? `: ${counts}` : ''}.`,
        };
      }
  }
};

// Agent role only ever sees their own calls — cross-operator comparison prompts
// would suggest access the system correctly refuses, so theirs are self-focused.
const AGENT_RESULTS_PROMPTS = [
  'What is my lowest-scoring category across these results?',
  'Which of these calls has my lowest overall score?',
  'What should I focus on improving based on these results?',
];

// ReactMarkdown component overrides — defined at module level so they are not recreated on every render
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const markdownComponents: any = {
  p: ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" sx={{ mb: 0.75, lineHeight: 1.6, fontSize: '0.8rem' }}>
      {children}
    </Typography>
  ),
  h1: ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" sx={{ mb: 0.5, mt: 1, fontWeight: 'bold', fontSize: '0.85rem', borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" sx={{ mb: 0.5, mt: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" sx={{ mb: 0.5, mt: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
      {children}
    </Typography>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <Box component="span" sx={{ fontWeight: 'bold' }}>{children}</Box>
  ),
  em: ({ children }: { children: React.ReactNode }) => (
    <Box component="span" sx={{ fontStyle: 'italic' }}>{children}</Box>
  ),
  li: ({ children }: { children: React.ReactNode }) => (
    <Typography component="li" variant="body2" sx={{ mb: 0.25, fontSize: '0.8rem', lineHeight: 1.5 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }: { children: React.ReactNode }) => (
    <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{children}</Box>
  ),
  ol: ({ children }: { children: React.ReactNode }) => (
    <Box component="ol" sx={{ pl: 2, my: 0.5 }}>{children}</Box>
  ),
  table: ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ overflowX: 'auto', my: 1, mx: -0.5 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', lineHeight: 1.4, '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left', verticalAlign: 'top', wordBreak: 'break-word', minWidth: '60px' }, '& th': { bgcolor: 'action.hover', fontWeight: 'bold', whiteSpace: 'nowrap' }, '& tr:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }: { children: React.ReactNode }) => <thead>{children}</thead>,
  tbody: ({ children }: { children: React.ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children: React.ReactNode }) => <tr>{children}</tr>,
  th: ({ children }: { children: React.ReactNode }) => <th>{children}</th>,
  td: ({ children }: { children: React.ReactNode }) => <td>{children}</td>,
  hr: () => <Divider sx={{ my: 1 }} />,
  blockquote: ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 1.5, my: 0.75, opacity: 0.85, fontSize: '0.8rem' }}>
      {children}
    </Box>
  ),
  code: ({ children, className }: { children: React.ReactNode; className?: string }) => {
    const isBlock = className?.startsWith('language-');
    return isBlock
      ? <Box component="code" sx={{ fontSize: '0.75rem' }}>{children}</Box>
      : <Box component="code" sx={{ fontSize: '0.75rem', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>{children}</Box>;
  },
  pre: ({ children }: { children: React.ReactNode }) => (
    <Box component="pre" sx={{ fontSize: '0.75rem', bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto', my: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </Box>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontSize: 'inherit', wordBreak: 'break-all' }}>
      {children}
    </Box>
  ),
};

// Helper function to render text with #call highlighting
const renderTextWithCallHighlight = (text: string) => {
  const parts = text.split(/(#call)/gi);
  return parts.map((part, index) => {
    if (part.toLowerCase() === '#call') {
      return (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: 'primary.main', // Blue background for both input and sent messages
            color: 'primary.contrastText', // White text for visibility
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: 'medium',
            border: '1px solid rgba(33, 150, 243, 0.5)',
          }}
        >
          {part}
        </Box>
      );
    }
    return part;
  });
};

interface ChatbotPaneProps {
  isExpanded: boolean;
  onToggle: () => void;
  onWidthChange?: (width: number) => void;
  callContext?: {
    callDetails: CallDetails;
    transcription: string;
  };
}

export const ChatbotPane: React.FC<ChatbotPaneProps> = ({ isExpanded, onToggle, onWidthChange, callContext }) => {
  const location = useLocation();
  const isCallDetailsPage = location.pathname.includes('/call/');

  const { resultsScope, chatScope, setChatScope, cadChat } = useCallContext();
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    authService.getUserAsync().then((user) => setUserRole(user?.role || ''));
  }, []);
  const hasCallScope = Boolean(isCallDetailsPage && callContext);
  // The CAD entry point exists only where the call page published its record: the call page,
  // with the agency's CAD records on. Attaching needs the same role the tab requires.
  const cadEntry = hasCallScope && cadChat ? cadChat : undefined;
  const canAttachCad = Boolean(cadEntry?.canEdit);
  const resultsScopeCount = resultsScope?.calls.length ?? 0;
  const hasResultsScope = resultsScopeCount > 0;
  // Fall back to "All calls" whenever the selected scope is no longer available
  // (e.g. navigating away from call details, or clearing the home-page filters).
  const effectiveScope: ChatScope =
    chatScope === 'call' && hasCallScope
      ? 'call'
      : chatScope === 'results' && hasResultsScope
        ? 'results'
        : 'all';

  // Incident number → callId, so response source chips can deep-link to call details.
  const incidentToCallId = useMemo(() => {
    const map = new Map<string, string>();
    resultsScope?.calls.forEach((c) => {
      if (c.incidentNumber) map.set(c.incidentNumber, c.callId);
    });
    return map;
  }, [resultsScope]);

  // Server analyzes at most MAX_CHAT_SCOPE_CALLS — labels must never over-claim.
  const resultsTruncated = Boolean(resultsScope && resultsScope.totalCount > resultsScopeCount);
  const resultsChipLabel = resultsTruncated
    ? `Current results (first ${resultsScopeCount} of ${resultsScope!.totalCount})`
    : `Current results (${resultsScopeCount})`;

  const suggestedPrompts =
    effectiveScope === 'results' && userRole === 'Agent'
      ? AGENT_RESULTS_PROMPTS
      : SUGGESTED_PROMPTS[effectiveScope];

  const getInitialMessage = (): ChatMessage => {
    const baseGreeting =
      'Hello, I am CallScore Assistant, your AI assistant for GovIQ. I can help you with questions about training materials, standards, and platform usage.';

    if (isCallDetailsPage && callContext) {
      const callSpecificGreeting =
        ' I can see you\'re viewing a call analysis. If you\'d like me to analyze this specific call, just include #call in your question and I\'ll reference the transcription and analysis results.\n\nExamples:\n- "Review this #call and explain possible improvement from customer service perspective only?"\n- "What specific training recommendations would help improve this #call performance based on the conversation?"\n- "Analyze the communication quality in this #call and suggest coaching points?"';

      return {
        id: '1',
        content: baseGreeting + callSpecificGreeting + '\n\nHow can I assist you today?',
        sender: 'bot',
        timestamp: new Date(),
      };
    }

    return {
      id: '1',
      content: baseGreeting + ' How can I assist you today?',
      sender: 'bot',
      timestamp: new Date(),
    };
  };

  const [messages, setMessages] = useState<ChatMessage[]>([getInitialMessage()]);
  const [inputValue, setInputValue] = useState('');
  const hasCallHashtag = inputValue.toLowerCase().includes('#call');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [pendingReplacement, setPendingReplacement] = useState<File | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  // The record this pane already asked to check, so an agency with the automatic check off gets
  // exactly one request per record from here.
  const checkRequestedRef = useRef<string | null>(null);

  // Every CAD reply is rendered from the record's live state; the message holds only the
  // reference, so nothing here has to be kept in step with the hook.
  const cadStages = useMemo(() => {
    const stages = new Map<string, CadStage>();
    if (cadEntry) {
      for (const m of messages) {
        if (m.cadCheck) stages.set(m.id, resolveCadStage(m.cadCheck, cadEntry));
      }
    }
    return stages;
  }, [messages, cadEntry]);
  const latestCheck = useMemo(() => [...messages].reverse().find((m) => m.cadCheck), [messages]);
  const cadBusy = !!latestCheck && cadStages.get(latestCheck.id)?.kind === 'progress';

  // Move focus into the pane when it opens — keyboard and screen-reader users
  // otherwise have to traverse the whole page to find it (WCAG 2.4.3).
  useEffect(() => {
    if (isExpanded) {
      const frame = window.requestAnimationFrame(() => textareaRef.current?.focus());
      return () => window.cancelAnimationFrame(frame);
    }
  }, [isExpanded]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Also when a CAD reply lands: it grows from one line to the findings in place.
  useEffect(() => {
    scrollToBottom();
  }, [messages, cadBusy]);

  // Update chatbot context when location or call context changes
  useEffect(() => {
    const newInitialMessage = getInitialMessage();
    setMessages([newInitialMessage]);
    setConversationId(''); // Reset conversation when context changes
    setError('');
    setPendingReplacement(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, callContext?.callDetails?.callId]);

  // Clear input when chatbot is closed
  useEffect(() => {
    if (!isExpanded) {
      setInputValue('');
      setError('');
    }
  }, [isExpanded]);

  const updateCadCheck = useCallback(
    (messageId: string, patch: Partial<CadCheckRef>) =>
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.cadCheck ? { ...m, cadCheck: { ...m.cadCheck, ...patch } } : m
        )
      ),
    []
  );

  /**
   * "Check CAD record for this call": posts the request as the user's line, with the file as a
   * chip when one was chosen, and a reply that follows the record. The file goes through the same
   * attach endpoint as the tab; the chat only ever references the attachment id.
   */
  const beginCadCheck = useCallback(
    (file?: File) => {
      if (!cadEntry) return;
      setChatScope('call');
      const now = Date.now();
      const userMessage: ChatMessage = {
        id: now.toString(),
        content: CAD_CHECK_MESSAGE,
        sender: 'user',
        timestamp: new Date(),
        attachment: file ? { fileName: file.name, sizeBytes: file.size } : undefined,
      };
      const replyId = `${now}-cad`;
      const reply: ChatMessage = {
        id: replyId,
        content: '',
        sender: 'bot',
        timestamp: new Date(),
        cadCheck: {
          attachmentId: file ? null : (cadEntry.cad.record?.attachmentId ?? null),
          attaching: !!file,
          requestFailed: false,
          dismissed: false,
        },
      };
      setMessages((prev) => [...prev, userMessage, reply]);
      if (!file) return;

      void cadEntry.cad.attach(file).then((created) => {
        if (created) {
          updateCadCheck(replyId, { attaching: false, attachmentId: created.attachmentId });
        } else {
          // The service layer already showed why.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === replyId
                ? {
                    ...m,
                    cadCheck: undefined,
                    content: 'The CAD record could not be attached. Check the file and try again.',
                  }
                : m
            )
          );
        }
      });
    },
    [cadEntry, setChatScope, updateCadCheck]
  );

  /** A chosen PDF: validated, confirmed when it would replace a record, then attached. */
  const handleCadFileChosen = useCallback(
    (file: File) => {
      if (!cadEntry) return;
      const problem = validatePdf(file);
      if (problem) {
        notificationService.error('Cannot Attach File', problem);
      } else if (cadEntry.cad.record) {
        setPendingReplacement(file);
      } else {
        beginCadCheck(file);
      }
    },
    [cadEntry, beginCadCheck]
  );

  const handleCadAction = useCallback(() => {
    if (!cadEntry) return;
    // No record yet: attaching one is the action, so go straight to the file picker.
    if (!cadEntry.cad.record && cadEntry.canEdit) {
      attachInputRef.current?.click();
      return;
    }
    beginCadCheck();
  }, [cadEntry, beginCadCheck]);

  // The latest check's two side effects on the CAD workflow, both shared with the tab through the
  // hook: reading a finished check (an audited read, once per check), and asking for the check
  // when the agency does not run it automatically (once per record from here).
  useEffect(() => {
    const ref = latestCheck?.cadCheck;
    if (!ref || !cadEntry || ref.attaching || !ref.attachmentId) return;
    const { cad, canEdit } = cadEntry;
    const record = cad.record;
    if (record?.attachmentId !== ref.attachmentId || record.extractionStatus !== 'Extracted') return;

    if (record.verificationStatus === 'Verified') {
      // After a failed load only an explicit retry (the tab's "Load again") asks again; the hook
      // clears its marker on failure, so asking here would loop.
      if (!cad.verificationLoadError) cad.loadVerification();
      return;
    }
    if (
      record.verificationStatus === 'NotRun' &&
      canEdit &&
      cad.pollState !== 'polling' &&
      !ref.requestFailed &&
      checkRequestedRef.current !== record.attachmentId
    ) {
      checkRequestedRef.current = record.attachmentId;
      const messageId = latestCheck.id;
      void cad.requestVerification().then((queued) => {
        if (!queued) updateCadCheck(messageId, { requestFailed: true });
      });
    }
  }, [latestCheck, cadEntry, updateCadCheck]);

  /** An apply from a check message: confirm in the conversation, then re-read the call as the tab does. */
  const handleCadApplied = useCallback(
    async (messageId: string, outcome: CadApplyOutcome) => {
      updateCadCheck(messageId, { dismissed: true });
      const content =
        outcome.appliedLabels.length > 0
          ? `Call record updated: ${outcome.appliedLabels.join(', ')}. The change is in the audit trail.`
          : outcome.changed
            ? 'The call record already has these values from the CAD record.'
            : 'No fields were applied to the call record.';
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-applied`, content, sender: 'bot', timestamp: new Date() },
      ]);
      if (outcome.changed && cadEntry) {
        await cadEntry.onCallRecordApplied(outcome.operatorChanged);
      }
    },
    [updateCadCheck, cadEntry]
  );

  let composerPlaceholder =
    'Ask CallScore about training materials, standards, or platform usage...';
  if (cadEntry && effectiveScope === 'call') {
    composerPlaceholder = canAttachCad
      ? 'Ask about this call, or attach a CAD record...'
      : 'Ask about this call...';
  } else if (isCallDetailsPage && callContext) {
    composerPlaceholder =
      'Ask CallScore about training materials, standards, or use #call for call-specific insights...';
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() && !isLoading) {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content: inputValue,
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      const query = inputValue;
      setInputValue('');
      setIsLoading(true);
      setError('');

      try {
        // "This call" scope (or the legacy #call hashtag) attaches the viewed call.
        // Call context wins over the results scope, mirroring backend precedence.
        const includesCallHashtag = query.includes('#call');
        const shouldIncludeCallContext =
          callContext && (effectiveScope === 'call' || (isCallDetailsPage && includesCallHashtag));

        // Build conversation history from prior turns (skip the greeting and any CAD reply
        // still in progress, cap at 10 turns). A CAD reply contributes what it says right now.
        const conversationHistory: ConversationMessage[] = messages
          .filter((m) => m.id !== '1')
          .flatMap((m): ConversationMessage[] => {
            const stage = m.cadCheck ? cadStages.get(m.id) : undefined;
            if (m.cadCheck && (!stage || stage.kind === 'progress')) return [];
            return [
              {
                role: m.sender === 'user' ? 'user' : 'assistant',
                content: stage && stage.kind !== 'progress' ? stage.content : m.content,
              },
            ];
          })
          .slice(-20);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const requestPayload: any = {
          query,
          conversationId: conversationId || undefined,
          conversationHistory,
        };

        if (shouldIncludeCallContext) {
          // A read CAD record rides along by id only; the server loads it for the verified call
          // and puts the extracted fields and the check into the prompt itself.
          const cadRecord = cadEntry?.cad.record;
          requestPayload.callContext = {
            callId: callContext.callDetails.callId,
            transcription: callContext.transcription,
            analysisResults: {
              overallScore: callContext.callDetails.overallScore,
              detailedScores: callContext.callDetails.detailedScoresJson,
              comprehensiveFeedback: callContext.callDetails.comprehensiveFeedback,
            },
            cadAttachmentId:
              cadRecord?.extractionStatus === 'Extracted' ? cadRecord.attachmentId : undefined,
          };
        } else if (effectiveScope === 'results' && resultsScope) {
          // "Current results": send the viewed result set's IDs. The server re-validates
          // every ID (cap, tenant-DB reload, operator-scope filter) — this is a hint,
          // never an authorization.
          requestPayload.scopeCallIds = resultsScope.calls.map((c) => c.callId);
        }

        const response = await chatbotService.sendMessage(requestPayload);

        // The record was referenced but had nothing to include (being read again): say so with
        // the answer rather than let a transcript-only reply pass as one that read the record.
        const cadLeftOut =
          !!requestPayload.callContext?.cadAttachmentId && response.cadRecordIncluded === false;
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: cadLeftOut
            ? `${response.response}\n\n_The CAD record is being read again, so this answer used the transcript only. Ask again once the CAD Record tab shows it as read._`
            : response.response,
          sender: 'bot',
          timestamp: new Date(response.timestamp),
          conversationId: response.conversationId,
          sources: response.sources,
        };

        setMessages((prev) => [...prev, botMessage]);
        setConversationId(response.conversationId);
      } catch (_error) {
        setError('Failed to send message. Please try again.');

        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content:
            "I apologize, but I'm experiencing technical difficulties. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClearChat = () => {
    setMessages([getInitialMessage()]);
    setConversationId('');
    setError('');
  };

  // --- Resize logic ---
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Drawer is anchored to the right, so width = viewport width - mouse X
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - e.clientX));
      setDrawerWidth(newWidth);
      onWidthChange?.(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Attach to document so dragging works even if cursor leaves the handle
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, onWidthChange]);

  // Reset width when drawer closes
  useEffect(() => {
    if (!isExpanded) {
      setDrawerWidth(DEFAULT_WIDTH);
      onWidthChange?.(DEFAULT_WIDTH);
    }
  }, [isExpanded, onWidthChange]);

  return (
    <>
      {/* Chatbot Drawer */}
      <Drawer
        anchor="right"
        variant="persistent"
        open={isExpanded}
        sx={{
          position: 'fixed',
          zIndex: 1200,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderLeft: 'none',
            position: 'fixed',
            height: '100vh',
            right: 0,
            transition: isResizing ? 'none' : 'width 0.3s ease',
            overflow: 'hidden',
          },
        }}
      >
        {/* Resize handle on the left edge */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '6px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              '& .resize-indicator': {
                opacity: 1,
                bgcolor: 'primary.main',
              },
            },
            ...(isResizing && {
              '& .resize-indicator': {
                opacity: 1,
                bgcolor: 'primary.main',
              },
            }),
          }}
        >
          <Box
            className="resize-indicator"
            sx={{
              width: '3px',
              height: '48px',
              borderRadius: '2px',
              bgcolor: 'divider',
              opacity: 0.5,
              transition: 'opacity 0.2s, background-color 0.2s',
            }}
          />
        </Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SupportAgentOutlined sx={{ mr: 1 }} />
            <Typography variant="h6">CallScore Assistant</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleClearChat}
              aria-label="Clear chat"
              sx={{ color: 'primary.contrastText', mr: 1 }}
              title="Clear chat history"
            >
              <Refresh />
            </IconButton>
            <IconButton
              onClick={onToggle}
              aria-label="Close chatbot"
              sx={{ color: 'primary.contrastText' }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Scope selector — which dataset the assistant answers over */}
        <Box
          role="group"
          aria-label="Ask about"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Ask about:
          </Typography>
          {hasCallScope && (
            <Chip
              label="This call"
              size="small"
              color={effectiveScope === 'call' ? 'primary' : 'default'}
              variant={effectiveScope === 'call' ? 'filled' : 'outlined'}
              onClick={() => setChatScope('call')}
              aria-pressed={effectiveScope === 'call'}
            />
          )}
          <Chip
            label="All calls"
            size="small"
            color={effectiveScope === 'all' ? 'primary' : 'default'}
            variant={effectiveScope === 'all' ? 'filled' : 'outlined'}
            onClick={() => setChatScope('all')}
            aria-pressed={effectiveScope === 'all'}
          />
          {hasResultsScope && (
            <Chip
              label={resultsChipLabel}
              size="small"
              color={effectiveScope === 'results' ? 'primary' : 'default'}
              variant={effectiveScope === 'results' ? 'filled' : 'outlined'}
              onClick={() => setChatScope('results')}
              aria-pressed={effectiveScope === 'results'}
            />
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>
            {error}
          </Alert>
        )}

        {/* Messages Area — polite live region so responses are announced (WCAG 4.1.3) */}
        <Box
          aria-live="polite"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <List sx={{ flexGrow: 1 }}>
            {messages.map((message) => {
              const stage = message.cadCheck ? cadStages.get(message.id) : undefined;
              return (
              <ListItem
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  px: 1,
                  py: 0.5,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    width: '100%',
                    bgcolor: message.sender === 'user' ? 'primary.main' : 'background.paper',
                    color: message.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2,
                    position: 'relative',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    {message.sender === 'bot' ? (
                      <SupportAgentOutlined sx={{ mr: 1, fontSize: '1rem', mt: 0.25 }} />
                    ) : (
                      <Person sx={{ mr: 1, fontSize: '1rem', mt: 0.25 }} />
                    )}
                    {message.sender === 'bot' ? (
                      <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: 'auto', overflowY: 'visible', wordBreak: 'break-word' }}>
                        {message.cadCheck && !stage ? (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            This CAD check is no longer available.
                          </Typography>
                        ) : stage?.kind === 'progress' ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={14} aria-hidden />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {stage.text}
                              {/* The figure changes every tick; kept out of the live region so
                                  the stage is announced once, not once per percent. */}
                              {stage.detail && (
                                <Box component="span" aria-hidden sx={{ ml: 0.5 }}>
                                  {stage.detail}
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        ) : stage?.kind === 'check' && cadEntry && callContext ? (
                          <CadCheckMessage
                            check={stage.check}
                            cadChat={cadEntry}
                            callDetails={callContext.callDetails}
                            onDismiss={() => updateCadCheck(message.id, { dismissed: true })}
                            onApplied={(outcome) => void handleCadApplied(message.id, outcome)}
                          />
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {stage ? stage.content : message.content}
                          </ReactMarkdown>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-line',
                            lineHeight: 1.5,
                          }}
                        >
                          {renderTextWithCallHighlight(message.content)}
                        </Typography>
                        {message.attachment && (
                          <Chip
                            icon={<PictureAsPdfOutlined aria-hidden />}
                            label={`${message.attachment.fileName} (${formatFileSize(message.attachment.sizeBytes)})`}
                            size="small"
                            variant="outlined"
                            sx={{
                              mt: 1,
                              maxWidth: '100%',
                              color: 'inherit',
                              borderColor: 'currentColor',
                              '& .MuiChip-icon': { color: 'inherit' },
                              '& .MuiChip-label': { overflowWrap: 'anywhere', whiteSpace: 'normal', py: 0.25 },
                              height: 'auto',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Sources for bot messages */}
                  {message.sender === 'bot' && message.sources && message.sources.length > 0 && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.5,
                        }}
                      >
                        <Source sx={{ fontSize: '0.75rem', mr: 0.5 }} />
                        Sources:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {message.sources.map((source, index) => {
                          // Incident-number sources from the results scope deep-link to the
                          // call — in a new tab, since in-pane navigation resets the chat.
                          const linkedCallId = incidentToCallId.get(source);
                          return (
                            <Chip
                              key={index}
                              label={source}
                              size="small"
                              variant="outlined"
                              clickable={Boolean(linkedCallId)}
                              onClick={
                                linkedCallId
                                  ? () => window.open(`/call/${linkedCallId}`, '_blank', 'noopener')
                                  : undefined
                              }
                              aria-label={
                                linkedCallId ? `Open call ${source} in a new tab` : undefined
                              }
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                ...(linkedCallId && {
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                }),
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.7,
                      fontSize: '0.75rem',
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Paper>
              </ListItem>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <ListItem
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  px: 1,
                  py: 0.5,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <SupportAgentOutlined sx={{ mr: 1, fontSize: '1rem' }} />
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2">CallScore is thinking...</Typography>
                </Paper>
              </ListItem>
            )}
          </List>
          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input Area */}
        <Box sx={{ p: 2 }}>
          {/* The CAD entry point: one action, offered whenever the call page has the feature and
              no check is already running. It attaches when nothing is attached, otherwise it
              shows the check for the record that is there. */}
          {cadEntry && (cadEntry.cad.record || cadEntry.canEdit) && !cadBusy && (
            <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Suggested for this call:
              </Typography>
              <Chip
                icon={<PictureAsPdfOutlined aria-hidden />}
                label="Check CAD record for this call"
                size="small"
                color="primary"
                variant="outlined"
                onClick={handleCadAction}
                sx={suggestionChipSx}
              />
            </Box>
          )}
          {/* Starter questions for the selected scope — shown while the chat is empty */}
          {messages.length <= 1 && (
            <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Try asking:
              </Typography>
              {suggestedPrompts.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setInputValue(prompt);
                    // Land focus on the input so keyboard users see the inserted text.
                    textareaRef.current?.focus();
                  }}
                  sx={suggestionChipSx}
                />
              ))}
            </Box>
          )}
          {/* "Include this call" quick-insert chip — visible on call details page.
              Redundant while the "This call" scope is selected (context always attaches). */}
          {isCallDetailsPage && callContext && effectiveScope !== 'call' && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={hasCallHashtag ? 'Call context included' : 'Include this call'}
                size="small"
                color={hasCallHashtag ? 'primary' : 'default'}
                variant={hasCallHashtag ? 'filled' : 'outlined'}
                onClick={() => {
                  if (!hasCallHashtag) {
                    setInputValue((prev) => (prev.trim() ? `${prev} #call` : '#call '));
                  }
                }}
                sx={{ cursor: hasCallHashtag ? 'default' : 'pointer', fontSize: '0.75rem' }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                flexGrow: 1,
                position: 'relative',
                minHeight: '56px',
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                '&:focus-within': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              }}
            >
              {/* Actual input textarea */}
              <textarea
                ref={textareaRef}
                aria-label="Message CallScore Assistant"
                placeholder={composerPlaceholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%',
                  minHeight: '56px',
                  maxHeight: '120px',
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  resize: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: 'inherit',
                }}
              />

              {/* Show #call indicator below input when detected */}
              {hasCallHashtag && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    backgroundColor: 'primary.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.200',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontWeight: 'medium',
                      fontSize: '12px',
                    }}
                  >
                    #call
                  </Box>
                  <Typography variant="caption" color="primary.main">
                    Call context will be included in your query
                  </Typography>
                </Box>
              )}
            </Box>
            {canAttachCad && (
              <>
                <HiddenPdfInput ref={attachInputRef} onFile={handleCadFileChosen} />
                <IconButton
                  onClick={() => attachInputRef.current?.click()}
                  disabled={cadBusy}
                  aria-label={
                    cadEntry?.cad.record
                      ? 'Attach a CAD record PDF, replacing the current one'
                      : 'Attach a CAD record PDF'
                  }
                  sx={{ minWidth: 44, minHeight: 44, alignSelf: 'flex-start' }}
                >
                  <AttachFileOutlined />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              color="primary"
              aria-label="Send message"
              sx={{
                minWidth: 44,
                minHeight: 44,
                alignSelf: 'flex-start',
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : <Send />}
            </IconButton>
          </Box>
          {canAttachCad && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              PDF only. The CAD file is stored with the call in your agency&apos;s storage.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {effectiveScope === 'results'
              ? resultsTruncated
                ? `CallScore answers strictly from the first ${resultsScopeCount} of ${resultsScope!.totalCount} calls in your current results`
                : `CallScore answers strictly from the ${resultsScopeCount} calls in your current results`
              : effectiveScope === 'call'
                ? 'CallScore answers about the call you are viewing — switch scope for broader questions'
                : isCallDetailsPage && callContext
                  ? 'CallScore can provide call-specific insights with #call hashtag or answer general questions'
                  : 'CallScore can help with training materials and platform guidance'}
          </Typography>
        </Box>
      </Drawer>

      {cadEntry && (
        <CadReplaceDialog
          pendingFile={pendingReplacement}
          currentFileName={cadEntry.cad.record?.originalFileName}
          onCancel={() => setPendingReplacement(null)}
          onConfirm={(file) => {
            setPendingReplacement(null);
            beginCadCheck(file);
          }}
        />
      )}
    </>
  );
};
